import { ButtonTertiary, ButtonPrimary, ButtonSecondary, RadioButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ExpandableQRCode } from "features/data-management/workspace/TODO-put-in-package/ExpandableQRCode"
import { MissionGroupIcon } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupIcon"
import { useContactLists } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/useContactListFunctions"
import { MissionGroupsManagePanel } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupsManagePanel"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState, useCallback } from "react"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"
import { setGlobalNotificationContext } from "user/profile/slice"
import * as Icons from "@nova/component-icons"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActivatedFireId } from "features/map/control/selectors"
import { ContactInputMultiSelect } from "themes/default-tailwind/buttons/ContactInput"
import { CreateMissionGroupModal } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupModals"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import classNames from "classnames"
import { SupportedRegions } from "@nova/constants"

export const multistreamUrl = ({ workspaceId }: { workspaceId: string }) =>
    `${window.location.origin}/live?workspaceId=${workspaceId}`
export const singleStreamUrl = ({ flightId }: { flightId: string }) =>
    `${window.location.origin}/live/${flightId}`
export const missionGroupUrl = ({
    workspaceId,
    groupId,
}: {
    workspaceId: string
    groupId: string
}) => `${window.location.origin}/live/mission-group?workspaceId=${workspaceId}&groupId=${groupId}`

export const ShareInactiveStreamLinkModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
    type,
}: {
    confirm: (a: {
        selectedEmails: string[]
        selectedPhoneNumbers: string[]
        selectedMissionGroupIds: string[]
    }) => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    type: "mission-group" | "all-streams"
}) => {
    const [view, setView] = useState<"share" | "manage">("share")
    const confirmationModal = useConfirmationModalContext()

    const { contactLists, isFetching } = useContactLists()

    const [selectedEmails, setSelectedEmails] = useState<string[]>([])
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([])
    const [selectedMissionGroupIds, setSelectedMissionGroupIds] = useState<string[]>([])
    return (
        <HighPriorityNovaModalContainer
            title={
                view === "manage"
                    ? "Manage Groups"
                    : type === "mission-group"
                      ? "Share Mission Group Streams"
                      : "Share All Streams"
            }
            hideTitleUnderline
            className="w-128 max-h-full"
            onReturn={view === "manage" ? () => setView("share") : undefined}
            bottomBar={
                <>
                    {view === "share" && (
                        <div className="w-full flex items-center justify-end gap-2">
                            <ButtonTertiary
                                title="Cancel"
                                onClick={cancel}
                                disabled={isPerformingAsyncConfirmCallback}
                            />
                            <ButtonPrimary
                                title="Share"
                                onClick={() =>
                                    confirm({
                                        selectedEmails,
                                        selectedPhoneNumbers,
                                        selectedMissionGroupIds,
                                    })
                                }
                                disabled={
                                    isPerformingAsyncConfirmCallback ||
                                    (!selectedEmails.length &&
                                        !selectedPhoneNumbers.length &&
                                        !selectedMissionGroupIds.length)
                                }
                            />
                        </div>
                    )}
                </>
            }
            closeModal={cancel}
        >
            {view === "manage" && <MissionGroupsManagePanel />}
            {view === "share" && (
                <>
                    <div className="flex flex-col pt-6 gap-6 overflow-hidden">
                        <div className="flex flex-col gap-1">
                            <div className="text-gray-100">Notify</div>
                            <ContactInputMultiSelect
                                enabledFeatures={["email", "phone"]}
                                emails={selectedEmails}
                                setEmails={setSelectedEmails}
                                phoneNumbers={selectedPhoneNumbers}
                                setPhoneNumbers={setSelectedPhoneNumbers}
                                placeholder="Add names, emails, or phone numbers"
                            />
                        </div>
                        <div className="flex flex-col gap-2 shrink min-h-0 overflow-hidden">
                            <div className="flex justify-between items-center">
                                <div className="text-gray-100">Mission Groups</div>
                                {!!contactLists.length && (
                                    <ButtonTertiary
                                        title="Manage"
                                        LeftIcon={Icons.PersonEdit}
                                        onClick={() => setView("manage")}
                                    />
                                )}
                            </div>
                            <div className="py-2.5 flex flex-col gap-4 border border-gray-700 rounded-lg overflow-hidden">
                                <ScrollableArea>
                                    {isFetching && (
                                        <div className="w-full h-full flex justify-center items-center py-3">
                                            <NovaSpinner width={20} height={20} />
                                        </div>
                                    )}
                                    {!isFetching && !contactLists.length && (
                                        <div className="flex justify-center py-3">
                                            <div className="flex flex-col items-center gap-2 w-2/3">
                                                <div className="text-lg">
                                                    Create your first Mission Group.
                                                </div>
                                                <div className="text-gray-100 text-center w-2/3">
                                                    Mission Groups help you easily share when you go
                                                    live.
                                                </div>
                                                <div>{/* Spacer */}</div>
                                                <ButtonSecondary
                                                    title="Create Mission Group"
                                                    LeftIcon={Icons.Plus}
                                                    onClick={() =>
                                                        confirmationModal.confirm({
                                                            RenderComponent:
                                                                CreateMissionGroupModal,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {contactLists.map((group) => (
                                        <div
                                            key={group.id}
                                            className="hover:bg-white-100 cursor-pointer flex gap-3 items-center px-2 py-1.5"
                                            onClick={() => {
                                                setSelectedMissionGroupIds((prev) => {
                                                    if (prev.includes(group.id)) {
                                                        return prev.filter((g) => g !== group.id)
                                                    }
                                                    return [...prev, group.id]
                                                })
                                            }}
                                        >
                                            <MissionGroupIcon groupId={group.id} size="32" />
                                            <div className="grow shrink min-w-0 truncate">
                                                {group.name}
                                            </div>
                                            <TriCheckbox
                                                state={
                                                    selectedMissionGroupIds.includes(group.id)
                                                        ? "checked"
                                                        : "unchecked"
                                                }
                                            />
                                        </div>
                                    ))}
                                </ScrollableArea>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </HighPriorityNovaModalContainer>
    )
}

export type ShareActiveLiveStreamArgs =
    | {
          type: StreamType
          selectedEmails: string[]
          selectedPhoneNumbers: string[]
          selectedMissionGroupIds?: never
      }
    | {
          type: "mission-groups"
          selectedEmails?: never
          selectedPhoneNumbers?: never
          selectedMissionGroupIds: string[]
      }
export type StreamType = "all-stream" | "single-stream"
export const ShareActiveLiveStreamModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
    flightId,
}: {
    confirm: (a: ShareActiveLiveStreamArgs) => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    flightId: string
}) => {
    const dispatch = useAppDispatch()
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const [view, setView] = useState<"share" | "manage">("share")
    const confirmationModal = useConfirmationModalContext()

    const { contactLists, isFetching } = useContactLists()

    const [tab, setTab] = useState<"direct" | "mission-group">("direct")
    const [type, setType] = useState<StreamType>("all-stream")
    const link =
        type === "all-stream" ? multistreamUrl({ workspaceId }) : singleStreamUrl({ flightId })
    const [selectedEmails, setSelectedEmails] = useState<string[]>([])
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([])
    const [selectedMissionGroupsIds, setSelectedMissionGroupsIds] = useState<string[]>([])
    return (
        <HighPriorityNovaModalContainer
            title={view === "manage" ? "Manage Groups" : "Share Live Stream"}
            hideTitleUnderline
            className="w-128 max-h-full"
            onReturn={view === "manage" ? () => setView("share") : undefined}
            bottomBar={
                <>
                    {view === "share" && (
                        <div className="w-full flex items-center justify-end gap-2">
                            <ButtonTertiary
                                title="Cancel"
                                onClick={cancel}
                                disabled={isPerformingAsyncConfirmCallback}
                            />
                            <ButtonPrimary
                                title="Share"
                                onClick={() => {
                                    if (tab === "direct") {
                                        confirm({
                                            type,
                                            selectedEmails,
                                            selectedPhoneNumbers,
                                        })
                                    } else {
                                        confirm({
                                            type: "mission-groups",
                                            selectedMissionGroupIds: selectedMissionGroupsIds,
                                        })
                                    }
                                }}
                                disabled={
                                    isPerformingAsyncConfirmCallback ||
                                    (tab === "direct" &&
                                        !selectedEmails.length &&
                                        !selectedPhoneNumbers.length) ||
                                    (tab === "mission-group" && !selectedMissionGroupsIds.length)
                                }
                            />
                        </div>
                    )}
                </>
            }
            closeModal={cancel}
        >
            {view === "manage" && <MissionGroupsManagePanel />}
            {view === "share" && (
                <>
                    <div className="flex">
                        <div className="flex-1">
                            <TabHeader
                                currentMode={tab}
                                mode="direct"
                                setActiveMode={() => setTab("direct")}
                                title="Direct"
                            />
                        </div>
                        <div className="flex-1">
                            <TabHeader
                                currentMode={tab}
                                mode="mission-group"
                                setActiveMode={() => setTab("mission-group")}
                                title="Mission Group"
                            />
                        </div>
                    </div>
                    {tab === "direct" && (
                        <div className="flex flex-col gap-6 py-6">
                            <div className="flex flex-col gap-1">
                                <div className="text-gray-100">Notify</div>
                                <ContactInputMultiSelect
                                    enabledFeatures={["email", "phone"]}
                                    emails={selectedEmails}
                                    setEmails={setSelectedEmails}
                                    phoneNumbers={selectedPhoneNumbers}
                                    setPhoneNumbers={setSelectedPhoneNumbers}
                                    placeholder="Add names, emails, or phone numbers"
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="text-gray-100">Type</div>
                                <div
                                    className="flex gap-3 cursor-pointer"
                                    onClick={() => setType("all-stream")}
                                >
                                    <RadioButton
                                        isSelected={type === "all-stream"}
                                        onClick={() => {}}
                                    />
                                    <div className="flex flex-col gap-0.5">
                                        <div
                                            className={classNames({
                                                "text-gray-50": type !== "all-stream",
                                                "text-white": type === "all-stream",
                                            })}
                                        >
                                            All Stream
                                        </div>
                                        <div className="text-gray-300">
                                            Notifies people with a link to your All Streams page.
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="flex gap-3 cursor-pointer"
                                    onClick={() => setType("single-stream")}
                                >
                                    <RadioButton
                                        isSelected={type === "single-stream"}
                                        onClick={() => {}}
                                    />
                                    <div className="flex flex-col gap-0.5">
                                        <div
                                            className={classNames({
                                                "text-gray-50": type !== "single-stream",
                                                "text-white": type === "single-stream",
                                            })}
                                        >
                                            Single Stream
                                        </div>
                                        <div className="text-gray-300">
                                            Notifies people with a link to this stream only.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-16">
                                <div className="grow min-w-0 flex flex-col gap-3">
                                    <div className="text-gray-100">
                                        Scan the QR code on your phone to watch{" "}
                                        {type === "all-stream"
                                            ? "all streams in your workspace."
                                            : "this individual stream."}
                                    </div>
                                    <ButtonTertiary
                                        title="Copy Link"
                                        LeftIcon={Icons.Copy}
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(link)
                                                .then(() => {
                                                    dispatch(
                                                        setGlobalNotificationContext({
                                                            message: `Copied to clipboard.`,
                                                            status: GlobalNotificationStatus.success,
                                                            timeoutInMs: 3000,
                                                        })
                                                    )
                                                })
                                                .catch(() => {
                                                    dispatch(
                                                        setGlobalNotificationContext({
                                                            message: `Failed to copy, please try again.`,
                                                            status: GlobalNotificationStatus.error,
                                                            timeoutInMs: 3000,
                                                        })
                                                    )
                                                })
                                        }
                                    />
                                </div>
                                <ExpandableQRCode
                                    title={
                                        type === "all-stream" ? "All Streams Link" : "Stream Link"
                                    }
                                    value={link}
                                    size={100}
                                    className="shrink-0"
                                />
                            </div>
                        </div>
                    )}
                    {tab === "mission-group" && (
                        <div className="flex flex-col gap-2 shrink min-h-0 overflow-hidden py-6">
                            <div className="flex justify-between items-center">
                                <div className="text-gray-100">Mission Groups</div>
                                {!!contactLists.length && (
                                    <ButtonTertiary
                                        title="Manage"
                                        LeftIcon={Icons.PersonEdit}
                                        onClick={() => setView("manage")}
                                    />
                                )}
                            </div>
                            <div className="py-2.5 flex flex-col gap-4 border border-gray-700 rounded-lg overflow-hidden">
                                <ScrollableArea>
                                    {isFetching && (
                                        <div className="w-full h-full flex justify-center items-center py-3">
                                            <NovaSpinner width={20} height={20} />
                                        </div>
                                    )}
                                    {!isFetching && !contactLists.length && (
                                        <div className="flex justify-center py-3">
                                            <div className="flex flex-col items-center gap-2 w-2/3">
                                                <div className="text-lg">
                                                    Create your first Mission Group.
                                                </div>
                                                <div className="text-gray-100 text-center w-2/3">
                                                    Mission Groups help you easily share when you go
                                                    live.
                                                </div>
                                                <div>{/* Spacer */}</div>
                                                <ButtonSecondary
                                                    title="Create Mission Group"
                                                    LeftIcon={Icons.Plus}
                                                    onClick={() =>
                                                        confirmationModal.confirm({
                                                            RenderComponent:
                                                                CreateMissionGroupModal,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {contactLists.map((group) => (
                                        <div
                                            key={group.id}
                                            className="hover:bg-white-100 cursor-pointer flex gap-3 items-center px-2 py-1.5"
                                            onClick={() => {
                                                setSelectedMissionGroupsIds((prev) => {
                                                    if (prev.includes(group.id)) {
                                                        return prev.filter((g) => g !== group.id)
                                                    }
                                                    return [...prev, group.id]
                                                })
                                            }}
                                        >
                                            <MissionGroupIcon groupId={group.id} size="32" />
                                            <div className="grow shrink min-w-0 truncate">
                                                {group.name}
                                            </div>
                                            <TriCheckbox
                                                state={
                                                    selectedMissionGroupsIds.includes(group.id)
                                                        ? "checked"
                                                        : "unchecked"
                                                }
                                            />
                                        </div>
                                    ))}
                                </ScrollableArea>
                            </div>
                        </div>
                    )}
                </>
            )}
        </HighPriorityNovaModalContainer>
    )
}

export const useShareLiveStream = () => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const shareLiveStream = useCallback(
        async ({
            type,
            flightId,
            selectedEmails,
            selectedPhoneNumbers,
            selectedMissionGroupIds,
        }:
            | {
                  type: "all-streams"
                  flightId?: never
                  selectedEmails: string[]
                  selectedPhoneNumbers: string[]
                  selectedMissionGroupIds: string[]
              }
            | {
                  type: "single-stream"
                  flightId: string
                  selectedEmails: string[]
                  selectedPhoneNumbers: string[]
                  selectedMissionGroupIds?: never
              }
            | {
                  type: "mission-group"
                  flightId: string
                  selectedEmails?: never
                  selectedPhoneNumbers?: never
                  selectedMissionGroupIds: string[]
              }) => {
            if (!workspaceId) return
            setIsLoading(true)
            try {
                if (type === "all-streams") {
                    await fetchApiGateway("post /org/:orgId/share-all-streams", {
                        params: {
                            orgId: workspaceId,
                        },
                        body: {
                            emails: selectedEmails,
                            phoneNumbers: selectedPhoneNumbers,
                            missionGroupIds: selectedMissionGroupIds,
                        },
                    })
                } else if (type === "single-stream") {
                    if (!projectId) return
                    await fetchApiGateway(
                        "post /org/:orgId/project/:projectId/layer/:layerId/share-stream",
                        {
                            params: {
                                orgId: workspaceId,
                                projectId,
                                layerId: flightId,
                            },
                            body: {
                                emails: selectedEmails,
                                phoneNumbers: selectedPhoneNumbers,
                            },
                        }
                    )
                } else {
                    await fetchApiGateway("post /org/:orgId/mission-group/streams", {
                        params: {
                            orgId: workspaceId,
                        },
                        body: {
                            missionGroupsIds: selectedMissionGroupIds,
                            layerId: flightId,
                        },
                        region: SupportedRegions.Canada, // needs to be canada-only to reach canadian redis
                    })
                }
                dispatch(
                    setGlobalNotificationContext({
                        status: GlobalNotificationStatus.success,
                        message: "Successfully shared stream link.",
                        timeoutInMs: 4000,
                    })
                )
            } finally {
                setIsLoading(false)
            }
        },
        [dispatch, projectId, workspaceId]
    )
    return { shareLiveStream, isLoading }
}
